// ShellDiagnosticStamp
//
// Tiny always-on diagnostic card for /offline-control that answers exactly
// one question: "Is this app currently running the hosted Roava web app or
// the copy bundled inside the Xcode binary?"
//
// Added in v7.3 b36 alongside the shell policy change (`on-failure` is now
// remote-first). When the orange Bundled Mode chip appears, this card
// explains why in concrete terms.

import { useEffect, useState } from "react";
import { isNative } from "@/lib/native";
import { BUILD_ID, shortBuildId } from "@/lib/build-info";
import {
  getShellConfig,
  getShellMode,
  onShellModeChange,
  type ShellConnectionType,
  type ShellMode,
  type ShellModeReason,
} from "@/lib/native/shell-mode";
import { useReachability } from "@/lib/connectivity/reachability";

type NativeInfo = { version?: string; build?: string };

function reasonLabel(r: ShellModeReason): string {
  switch (r) {
    case "online-remote":         return "Online — using hosted Roava";
    case "remote-enabled":        return "Remote-enabled";
    case "debug-override":        return "Debug override (force remote)";
    case "remote-probe-failed":   return "Hosted Roava unreachable — using bundled";
    case "remote-navigation-failed":
                                  return "Hosted load failed mid-flight — using bundled";
    case "local-only":            return "Local-only policy";
    case "local-first":           return "Local-first (legacy)";
    case "local-failed":          return "Local load failed — tried remote";
    case "manual-retry":          return "Manual retry";
    case "boot":
    case "boot-pre-remote":       return "Booting…";
    case "remote_success_cleared":return "Back online";
    default:                      return String(r);
  }
}

function connLabel(c: ShellConnectionType): string {
  switch (c) {
    case "wifi":     return "Wi-Fi";
    case "cellular": return "Cellular";
    case "wired":    return "Wired";
    case "other":    return "Other";
    case "none":     return "No link";
    default:         return "Unknown";
  }
}

export function ShellDiagnosticStamp({ className = "" }: { className?: string }) {
  const [native, setNative] = useState<NativeInfo | null>(null);
  const [mode, setMode] = useState<ShellMode>("remote");
  const [modeReason, setModeReason] = useState<ShellModeReason>("boot");
  const [conn, setConn] = useState<ShellConnectionType>("unknown");
  const [remoteUrl, setRemoteUrl] = useState<string>("https://roava.lovable.app");
  const reach = useReachability();

  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        setNative({ version: info.version, build: info.build });
      } catch { /* not available */ }
    })();
    void getShellConfig().then((c) => {
      if (c?.remoteUrl) setRemoteUrl(c.remoteUrl);
    });
    void getShellMode().then((s) => {
      setMode(s.mode);
      setModeReason(s.modeReason);
      setConn(s.connectionType);
    });
    const off = onShellModeChange((s) => {
      setMode(s.mode);
      setModeReason(s.modeReason);
      setConn(s.connectionType);
    });
    return () => { off(); };
  }, []);

  const appPart = native
    ? `Roava ${native.version ?? "?"} (build ${native.build ?? "?"})`
    : isNative() ? "Native info unavailable" : "Web preview";

  const bundledStamp = `${shortBuildId()} · ${BUILD_ID}`;
  const reachLabel =
    reach.backendReachable === true ? "Yes"
    : reach.backendReachable === false ? "No"
    : "Checking…";

  const dot = mode === "remote" ? "bg-emerald-500" : "bg-amber-500";

  return (
    <div className={`rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)]/60 p-3 ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`inline-block size-2 rounded-full ${dot}`} aria-hidden />
        <h3 className="text-xs font-semibold text-[var(--roava-text)]">
          Shell diagnostic
        </h3>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-wide text-[var(--roava-text-muted)]">
          {mode}
        </span>
      </div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[11px] text-[var(--roava-text)]">
        <dt className="text-[var(--roava-text-muted)]">Native</dt>
        <dd className="font-mono break-all">{appPart}</dd>

        <dt className="text-[var(--roava-text-muted)]">Mode reason</dt>
        <dd>{reasonLabel(modeReason)}</dd>

        <dt className="text-[var(--roava-text-muted)]">Connection</dt>
        <dd>{connLabel(conn)}</dd>

        <dt className="text-[var(--roava-text-muted)]">Hosted reachable</dt>
        <dd>{reachLabel}</dd>

        <dt className="text-[var(--roava-text-muted)]">Hosted URL</dt>
        <dd className="font-mono break-all text-[var(--roava-text-muted)]">{remoteUrl}</dd>

        <dt className="text-[var(--roava-text-muted)]">Bundled web</dt>
        <dd className="font-mono break-all">{bundledStamp}</dd>
      </dl>
      <p className="mt-2 text-[10px] leading-snug text-[var(--roava-text-muted)]">
        {mode === "remote"
          ? "You are seeing the hosted Roava web app."
          : "You are seeing the web copy bundled inside this native build. Hosted updates won't appear until the shell switches back to remote."}
      </p>
    </div>
  );
}
